import React from 'react';
import { get } from 'lodash';
import { Path, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { ISimulationPayload } from '@schemas/scenarios-simulator/types';
import { HeroIcon, TooltipComponent } from '@clarke-energia/foton-v2';
import { ScenarioBasePath } from '@hooks/scenarios-simulator/types';
import { HistoryKeys } from '@components/molecules/scenarios-simulator/SectionFields/history';

const EditableSectionFieldKeys = {
  'Sobre a distribuição': 'AboutDistribution',
  'Histórico de consumo': 'ConsumptionHistory',
  'Histórico de demanda': 'DemandHistory',
  'Histórico de consumo ponta': 'PeakConsumptionHistory',
  'Editar dados sobre o histórico de consumo fora de ponta': 'OffPeakConsumptionHistory',
  'Editar dados sobre o histórico de consumo ponta': 'PeakDemandHistory',
  'Histórico de demanda fora de ponta': 'OffPeakDemandHistory',
  'Demanda contratada': 'ContractedDemand',
  'Horário de funcionamento': 'OperatingHours',
  'Historico de consumo e demanda': 'History',
  'Datas do contrato': 'ContractDates',
  'Sobre gestão': 'AboutManagement',
  'Sobre energia': 'AboutEnergy',
} as const;

interface EditableToggleSectionProps {
  title: string;
  unitNum: number;
  scenarioBasePath: ScenarioBasePath;
  isTargetScenario: boolean;
  subtitle?: string;
  children?: React.ReactNode;
  fieldKey?: string;
  className?: string;
  tooltipDescription?: string;
  customFieldPath?: string;
  errorFields?: string[];
  keys?: HistoryKeys;
  initialOpen?: boolean;
}

export const EditableToggleSection: React.FC<EditableToggleSectionProps> = ({
  children,
  title,
  subtitle,
  fieldKey,
  unitNum,
  scenarioBasePath,
  isTargetScenario,
  className,
  tooltipDescription,
  customFieldPath,
  errorFields,
  keys,
  initialOpen = false,
}) => {
  const { control, setValue } = useFormContext<ISimulationPayload>();
  const { errors } = useFormState<ISimulationPayload>({ control });

  const key =
    fieldKey ||
    keys ||
    EditableSectionFieldKeys[title as keyof typeof EditableSectionFieldKeys] ||
    title.replace(/\s/g, '');
  const fieldPath =
    customFieldPath ?? `${scenarioBasePath}.scenarioPayload.${unitNum}.presetPayload.aux__useEditableSection_${key}`;
  const isEnabled = useWatch({
    control,
    name: fieldPath as Path<ISimulationPayload>,
    defaultValue: initialOpen,
  });

  React.useEffect(() => {
    if (isTargetScenario && initialOpen) {
      setValue(fieldPath as Path<ISimulationPayload>, true, { shouldDirty: false });
    }
  }, [fieldPath]);

  const hasError = errorFields?.some((field) => !!get(errors, field)) ?? false;

  React.useEffect(() => {
    if (isTargetScenario && hasError && !isEnabled) {
      setValue(fieldPath as Path<ISimulationPayload>, true, { shouldDirty: true });
    }
  }, [hasError]);

  if (!isTargetScenario) {
    return <>{children}</>;
  }

  const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>): any => {
    e.preventDefault();
    e.stopPropagation();
    setValue(fieldPath as Path<ISimulationPayload>, !isEnabled, { shouldDirty: true });
  };

  return (
    <section className={className ?? 'col-span-1 flex flex-col w-full h-12 min-h-fit gap-4'}>
      <div
        className={`bg-light-30 flex flex-col justify-between items-center gap-5 p-5 relative w-full h-fit border
        ${hasError ? 'border-orange-30' : 'border-brand-gray-10'}
     rounded-3xl overflow-hidden`}
      >
        {/* @ts-expect-error onClick type mismatch on div */}
        <div onClick={handleEditClick} className="flex justify-between items-center gap-5 w-full cursor-pointer">
          <div className="flex items-center gap-6 size-fit">
            <button
              type="button"
              onClick={handleEditClick}
              className="flex size-8 items-center justify-center gap-2.5 p-2 rounded-[5px] transition-all bg-brand-gray-10 cursor-pointer"
            >
              <HeroIcon icon="PencilSquareIcon" className="size-6 text-black" />
            </button>
            <div className="flex flex-col items-start gap-1">
              <span className={`text-sm font-bold ${!isEnabled && hasError ? 'text-orange-30' : 'text-black'}`}>
                {title}
              </span>
              {subtitle && <span className="text-xs text-brand-gray-60">{subtitle}</span>}
            </div>
            {!isEnabled && hasError && <HeroIcon icon="ExclamationCircleIcon" className="size-5 text-orange-30" />}
            {tooltipDescription && (
              <TooltipComponent
                side="right"
                content={tooltipDescription}
                icon={{
                  icon: 'InformationCircleIcon',
                }}
              />
            )}
          </div>
          <HeroIcon icon={isEnabled ? 'ChevronUpIcon' : 'ChevronDownIcon'} className="size-5 text-brand-gray-60" />
        </div>
        {isEnabled && (
          <div className={`bg-light-30 flex justify-between items-center gap-5 w-full h-fit overflow-hidden`}>
            {children}
          </div>
        )}
      </div>
    </section>
  );
};
